<template>
  <div class="pending-container">
    <div class="pending-card">
      <div class="icon-container">
        <svg class="pending-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <h1 class="pending-title">Account Under Review</h1>
      
      <div class="pending-message">
        <p class="main-message">
          Your account creation is currently being reviewed by our team.
        </p>
        <p class="sub-message">
          We'll notify you via email once your account has been approved and you can access the platform.
        </p>
      </div>

      <div class="email-verification" v-if="!isEmailVerified">
        <div class="verification-notice">
          <svg class="warning-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div>
            <p class="verification-title">Email Verification Required</p>
            <p class="verification-text">Please verify your email address to complete the registration process.</p>
          </div>
        </div>
        
        <button 
          @click="resendVerification" 
          :disabled="verificationLoading"
          class="verification-button"
        >
          <span v-if="!verificationLoading">Resend Verification Email</span>
          <span v-else class="loading-text">
            <div class="loading-spinner"></div>
            Sending...
          </span>
        </button>
        
        <p v-if="verificationMessage" class="verification-message" :class="verificationSuccess ? 'success' : 'error'">
          {{ verificationMessage }}
        </p>
      </div>

      <div class="user-info">
        <div class="info-item">
          <span class="info-label">Email:</span>
          <span class="info-value">{{ userEmail }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Status:</span>
          <span class="info-value status-pending">Pending Review</span>
        </div>
        <div class="info-item" v-if="userDisplayName">
          <span class="info-label">Name:</span>
          <span class="info-value">{{ userDisplayName }}</span>
        </div>
      </div>

      <div class="actions">
        <button @click="refreshStatus" :disabled="loading" class="refresh-button">
          <svg v-if="!loading" class="refresh-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 3v5h-5M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 21v-5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div v-else class="loading-spinner"></div>
          {{ loading ? 'Checking...' : 'Check Status' }}
        </button>
        
        <button @click="signOut" class="sign-out-button">
          Sign Out
        </button>
      </div>

      <div class="contact-info">
        <p class="contact-text">
          Need help? Contact us at 
          <a href="mailto:support@routebox.com" class="contact-link">support@routebox.com</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AuthService from '../services/AuthService'

export default {
  name: 'PendingPage',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const verificationLoading = ref(false)
    const verificationMessage = ref('')
    const verificationSuccess = ref(false)

    const user = computed(() => AuthService.getCurrentUser())
    const userEmail = computed(() => user.value?.email || '')
    const userDisplayName = computed(() => user.value?.displayName || '')
    const isEmailVerified = computed(() => user.value?.emailVerified || false)

    const refreshStatus = async () => {
      loading.value = true
      try {
        // Check if user has permissions now
        if (user.value) {
          const hasPermissions = await AuthService.hasAnyPermissions(user.value.uid)
          if (hasPermissions) {
            // User now has permissions, redirect to home
            router.push('/home')
            return
          }
        }
        
        // Reload the current user to check email verification
        await user.value?.reload()
        
      } catch (error) {
        console.error('Error refreshing status:', error)
      } finally {
        loading.value = false
      }
    }

    const resendVerification = async () => {
      verificationLoading.value = true
      verificationMessage.value = ''
      
      try {
        const result = await AuthService.resendEmailVerification()
        if (result.error) {
          verificationMessage.value = result.error
          verificationSuccess.value = false
        } else {
          verificationMessage.value = 'Verification email sent successfully!'
          verificationSuccess.value = true
        }
      } catch (error) {
        verificationMessage.value = 'Failed to send verification email. Please try again.'
        verificationSuccess.value = false
      } finally {
        verificationLoading.value = false
      }
    }

    const signOut = async () => {
      try {
        await AuthService.signOut()
        router.push('/login')
      } catch (error) {
        console.error('Error signing out:', error)
      }
    }

    onMounted(() => {
      // Check status on mount
      refreshStatus()
    })

    return {
      loading,
      verificationLoading,
      verificationMessage,
      verificationSuccess,
      userEmail,
      userDisplayName,
      isEmailVerified,
      refreshStatus,
      resendVerification,
      signOut
    }
  }
}
</script>

<style scoped>
.pending-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.pending-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 50px 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.icon-container {
  margin-bottom: 30px;
}

.pending-icon {
  width: 80px;
  height: 80px;
  color: #f39c12;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.pending-title {
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px 0;
  font-family: 'Sansation', sans-serif;
}

.pending-message {
  margin-bottom: 30px;
}

.main-message {
  font-size: 18px;
  color: #4a5568;
  margin: 0 0 10px 0;
  font-weight: 500;
}

.sub-message {
  font-size: 16px;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

.email-verification {
  background: #fff8dc;
  border: 1px solid #f6e05e;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
}

.verification-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 15px;
}

.warning-icon {
  width: 24px;
  height: 24px;
  color: #d69e2e;
  flex-shrink: 0;
  margin-top: 2px;
}

.verification-title {
  font-weight: 600;
  color: #744210;
  margin: 0 0 5px 0;
  font-size: 14px;
}

.verification-text {
  color: #975a16;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.verification-button {
  background: #d69e2e;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
}

.verification-button:hover:not(:disabled) {
  background: #b7791f;
}

.verification-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.verification-message {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 500;
}

.verification-message.success {
  color: #38a169;
}

.verification-message.error {
  color: #e53e3e;
}

.user-info {
  background: #f7fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #4a5568;
  font-size: 14px;
}

.info-value {
  color: #2d3748;
  font-size: 14px;
}

.status-pending {
  background: #fed7d7;
  color: #c53030;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 12px;
  margin-bottom: 30px;
}

.refresh-button, .sign-out-button {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.refresh-button {
  background: #0077c2;
  color: white;
  border: none;
}

.refresh-button:hover:not(:disabled) {
  background: #005fa3;
}

.refresh-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.sign-out-button {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.sign-out-button:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.refresh-icon {
  width: 16px;
  height: 16px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.contact-info {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
}

.contact-text {
  color: #718096;
  font-size: 14px;
  margin: 0;
}

.contact-link {
  color: #0077c2;
  text-decoration: none;
  font-weight: 600;
}

.contact-link:hover {
  text-decoration: underline;
}

/* Responsive design */
@media (max-width: 600px) {
  .pending-card {
    padding: 30px 20px;
    margin: 10px;
  }
  
  .pending-title {
    font-size: 24px;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .verification-notice {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .verification-text {
    text-align: center;
  }
}
</style>
